import router from '@/router'
import { login, refreshToken, getToken } from '@/api/auth'

const state = {
  userInfo: {
    username: '',
    account_type: '',
    company_id: '',
    company_name: ''
  },
  access_token: '',
  refresh_token: '',
  expires_at: '',
  tokenCheckTimer: null
}

const getters = {
  userInfo: state => state.userInfo,
  access_token: state => state.access_token,
  refresh_token: state => state.refresh_token,
  expires_at: state => state.expires_at,
  // 检查token是否即将过期
  isTokenExpiring: state => {
    if (!state.expires_at) {
      return true
    }

    // 将expires_at字符串转换为时间戳
    const expiryDate = new Date(state.expires_at)
    const now = new Date()

    // 计算剩余时间（毫秒）
    const timeRemaining = expiryDate - now

    // 如果剩余时间小于5分钟（300000毫秒），则认为即将过期
    return timeRemaining < 300000
  }
}

const mutations = {
  // 设置用户信息
  SET_USER_INFO(state, userInfo) {
    state.userInfo = userInfo
  },

  // 设置token
  SET_TOKEN(state, { access_token, refresh_token, expires_at }) {
    state.access_token = access_token
    state.refresh_token = refresh_token
    state.expires_at = expires_at

    // 存储到localStorage
    localStorage.setItem('access_token', access_token)
    localStorage.setItem('refresh_token', refresh_token)
    localStorage.setItem('expires_at', expires_at)
  },

  // 从localStorage加载token
  LOAD_TOKEN(state) {
    const access_token = localStorage.getItem('access_token')
    const refresh_token = localStorage.getItem('refresh_token')
    const expires_at = localStorage.getItem('expires_at')
    const username = localStorage.getItem('username')
    const account_type = localStorage.getItem('account_type')
    const company_id = localStorage.getItem('company_id')
    const company_name = localStorage.getItem('company_name')

    if (access_token && refresh_token && expires_at) {
      state.access_token = access_token
      state.refresh_token = refresh_token
      state.expires_at = expires_at
    }

    if (username) {
      state.userInfo = {
        username,
        account_type,
        company_id,
        company_name
      }
    }
  },

  // 清除token和用户信息
  CLEAR_TOKEN(state) {
    state.access_token = ''
    state.refresh_token = ''
    state.expires_at = ''
    state.userInfo = {
      username: '',
      account_type: '',
      company_id: '',
      company_name: ''
    }

    // 从localStorage清除
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem('username')
    localStorage.removeItem('account_type')
    localStorage.removeItem('company_id')
    localStorage.removeItem('company_name')
  }
}

const actions = {
  // 登录
  async login({ commit, dispatch }, userInfo) {
    try {
      const response = await login(userInfo)

      // 处理不同格式的响应对象
      let responseData = response
      let isSuccess = false

      // 检查是否是axios完整响应对象
      if (response && response.status === 200 && response.data) {
        // 如果是axios响应对象，提取data部分
        responseData = response.data
        isSuccess = responseData.code === 200 || responseData.code === "SUCCESS" || responseData.success === true
      } else {
        // 否则直接检查响应数据
        isSuccess = response.code === 200 || response.code === "SUCCESS" || response.success === true
      }

      if (isSuccess || responseData.code === 0) {
        const { data } = responseData

        // 设置用户信息
        commit('SET_USER_INFO', {
          username: data.username,
          account_type: data.account_type,
          company_id: data.company_id,
          company_name: data.company_name
        })

        // 设置token
        commit('SET_TOKEN', {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at
        })

        // 存储用户信息到localStorage
        localStorage.setItem('username', data.username)
        localStorage.setItem('account_type', data.account_type)
        localStorage.setItem('company_id', data.company_id)
        localStorage.setItem('company_name', data.company_name)

        // 启动token过期检查定时器
        dispatch('startTokenExpiryCheck')

        // 获取并存储七牛和萤石云token
        try {
          const tokenResponse = await getToken()
          
          const { qiniu_token, ys_token } = tokenResponse

       
          localStorage.setItem('qiniu_token', qiniu_token)
          localStorage.setItem('ys_token', ys_token)
          
          console.log(localStorage.getItem('ys_token'))
        } catch (error) {
          console.error('获取七牛或萤石云token失败:', error)
        }

        return Promise.resolve(responseData)
      } else {
        // 登录失败，确保不会启动任何定时器或执行后续操作
        console.log('登录失败，不执行任何后续操作')
        // 确保清除任何可能存在的定时器
        dispatch('clearTokenCheckTimer')
        return Promise.reject(responseData || response)
      }
    } catch (error) {
      console.error('Login action error:', error)
      // 确保登录失败后不会有任何后续操作
      console.log('登录异常，确保清理资源并停止所有后续操作')

      // 如果捕获到的是axios错误对象，尝试提取有用信息
      if (error.response) {
        // 服务器返回了错误状态码
        return Promise.reject({
          code: error.response.status,
          message: (error.response.data && error.response.data.message) || '登录失败',
          response: error.response
        })
      } else if (error.request) {
        // 请求已发送但没有收到响应
        return Promise.reject({
          code: 503,
          message: '服务器无响应，请检查网络连接',
          request: error.request
        })
      } else {
        // 其他错误
        return Promise.reject({
          code: 500,
          message: error.message || '登录过程中发生错误',
          error: error
        })
      }
    }
  },

  // 刷新token
  async refreshToken({ commit, state }) {
    try {
      // 将参数名从refresh_token改为RefreshToken以符合后端API要求
      const response = await refreshToken({ RefreshToken: state.refresh_token })
      if (response.code === "SUCCESS" || response.code === 200) {
        const { data } = response

        // 更新token
        commit('SET_TOKEN', {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at
        })

        return Promise.resolve(response)
      } else {
        // 刷新失败，清除token并跳转到登录页
        commit('CLEAR_TOKEN')
        return Promise.reject(response)
      }
    } catch (error) {
      // 网络错误，清除token并跳转到登录页
      commit('CLEAR_TOKEN')
      return Promise.reject(error)
    }
  },

  // 启动token过期检查定时器
  startTokenExpiryCheck({ dispatch, state, getters }) {
    // 清除已有的定时器
    if (state.tokenCheckTimer) {
      clearInterval(state.tokenCheckTimer)
    }

    // 每30秒检查一次token是否即将过期
    const timer = setInterval(() => {
      if (getters.isTokenExpiring) {
        // 如果token即将过期，自动刷新
        dispatch('refreshToken')
          .then(() => {
            console.log('Token自动刷新成功')
          })
          .catch(error => {
            console.error('Token自动刷新失败:', error)
            // 刷新失败时清除token并跳转到登录页
            dispatch('logout')
            router.push('/login')
          })
      }
    }, 30000)

    // 存储定时器引用
    state.tokenCheckTimer = timer
  },

  // 清除token检查定时器
  clearTokenCheckTimer({ state }) {
    if (state.tokenCheckTimer) {
      clearInterval(state.tokenCheckTimer)
      state.tokenCheckTimer = null
    }
  },

  // 注销
  logout({ commit, dispatch }) {
    // 清除token检查定时器
    dispatch('clearTokenCheckTimer')
    commit('CLEAR_TOKEN')
  },

  // 初始化加载token
  initToken({ commit }) {
    commit('LOAD_TOKEN')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
